// Biology → Section 4: Inheritance, Variation and Survival (KS3, sourced
// from the Grade9Ready science PDF). Same rich-content template as
// biologySection1/2/3.js.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'biology/inheritance-variation-survival';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 4.1  DNA AND CHROMOSOMES
// =====================================================================
const DNA_TOPIC = {
  id: 'bio-4.1',
  title: 'DNA and Chromosomes',
  slug: 'dna-and-chromosomes',
  pageRef: 'Section 4 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is DNA?',
        body:
          'DNA (deoxyribonucleic acid) is the molecule that carries the instructions for building and running every living thing. It looks like a twisted ladder — a "double helix" — and is found in the nucleus of nearly every cell. The rungs of the ladder are made from four bases: A, T, C and G. The order of these bases is the genetic code.',
        check: {
          question: 'Where in the cell is most DNA found?',
          options: ['Cell membrane', 'Cytoplasm', 'Nucleus', 'Mitochondrion'],
          answer: 'Nucleus',
          explainer: 'Almost all DNA is packed into chromosomes inside the nucleus. (A tiny amount also lives in mitochondria.)',
        },
      },
      {
        heading: 'Genes, chromosomes and the genome',
        body:
          'A gene is a short section of DNA that codes for one feature (e.g. eye colour). DNA is wound up into long strands called chromosomes. Humans have 46 chromosomes, arranged in 23 pairs — one of each pair from each parent. The complete set of an organism\u2019s DNA is called its genome.',
        check: {
          question: 'How many chromosomes does a normal human body cell have?',
          options: ['23', '46', '64', '92'],
          answer: '46',
          explainer: '46 in total = 23 pairs. Sex cells (eggs and sperm) carry only 23 (one of each pair).',
        },
      },
      {
        heading: 'Sex chromosomes',
        body:
          'One pair of human chromosomes decides biological sex. Females are XX, males are XY. Every egg cell carries an X chromosome; sperm carry either an X or a Y. So the sperm decides whether the offspring is XX (female) or XY (male). The chance of a boy or a girl is roughly 50:50 each time.',
        check: {
          question: 'Which combination of sex chromosomes makes a male?',
          options: ['XX', 'XY', 'YY', 'X only'],
          answer: 'XY',
          explainer: 'Mum always passes an X. Dad can pass X (→ XX, girl) or Y (→ XY, boy).',
        },
      },
    ],
    keyPoints: [
      'DNA = double helix; bases A, T, C, G; in nucleus',
      'Gene = section of DNA coding for a feature',
      'Chromosome = long strand of DNA',
      'Humans: 46 chromosomes (23 pairs)',
      'Genome = complete DNA of an organism',
      'Sex: female = XX, male = XY',
    ],
    workedExamples: [
      {
        problem: 'Mum is XX, Dad is XY. Use a Punnett square to predict the chance of a boy.',
        steps: [
          { step: 1, instruction: 'List Mum\u2019s gametes', working: 'X, X' },
          { step: 2, instruction: 'List Dad\u2019s gametes', working: 'X, Y' },
          { step: 3, instruction: 'Make the 2×2 grid', working: 'XX, XX, XY, XY' },
          { step: 4, instruction: 'Count outcomes', working: '2 XX (girl) and 2 XY (boy) → 2/4 boy' },
        ],
        answer: 'Probability of a boy = 50%.',
        commonMistake: 'Saying it changes from child to child — each pregnancy is an independent 50:50 event.',
      },
    ],
    whyItWorks:
      'DNA is the universal language of life. Every species — humans, bacteria, oak trees — uses the same four bases. The differences between species (and between you and your sibling) are simply down to the order those bases are arranged in.',
  },
  glossary: [
    { term: 'DNA', definition: 'Deoxyribonucleic acid — the molecule carrying genetic information.' },
    { term: 'Gene', definition: 'A section of DNA that codes for a feature (e.g. eye colour).' },
    { term: 'Chromosome', definition: 'A long, coiled strand of DNA found in the nucleus.' },
    { term: 'Genome', definition: 'The complete set of DNA in an organism.' },
    { term: 'Base', definition: 'One of four chemicals (A, T, C, G) that make up the rungs of the DNA ladder.' },
    { term: 'Nucleus', definition: 'The cell organelle that holds the DNA.' },
    { term: 'Sex chromosomes', definition: 'The pair (XX or XY) that decides biological sex.' },
  ],
  examQuestion: {
    prompt:
      '(a) Describe the structure of DNA. (b) Explain how the sex of a baby is determined at fertilisation. (c) State the difference between a gene and a chromosome. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'DNA is a double helix made from two strands twisted together',
      'Rungs are made from base pairs (A-T and C-G)',
      'Egg always carries X; sperm carries either X or Y',
      'X sperm + X egg = XX female; Y sperm + X egg = XY male',
      'Gene = short section of DNA; chromosome = long strand of DNA carrying many genes',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.1.w1', type: 'text', question: 'DNA stands for ___ acid.', answer: 'deoxyribonucleic', hint1: 'Long word', hint2: '16 letters', hint3: 'Starts with d' },
      { id: 'bio-4.1.w2', type: 'choice', question: 'Where is DNA mostly found?', options: ['Cytoplasm', 'Membrane', 'Nucleus', 'Cell wall'], answer: 'Nucleus', hint1: 'Control centre', hint2: '7 letters', hint3: 'In animal + plant cells' },
      { id: 'bio-4.1.w3', type: 'choice', question: 'Female sex chromosomes are…', options: ['XX', 'XY', 'YY', 'XYY'], answer: 'XX', hint1: 'Two of the same', hint2: 'X repeated', hint3: 'Not Y' },
    ],
    level1: [
      { id: 'bio-4.1.1', type: 'number', question: 'How many chromosomes are in a human body cell?', answer: '46', hint1: 'Two digits', hint2: '23 pairs', hint3: 'Even number' },
      { id: 'bio-4.1.2', type: 'choice', question: 'A short section of DNA that codes for a feature is a…', options: ['Chromosome', 'Gene', 'Protein', 'Cell'], answer: 'Gene', hint1: '4 letters', hint2: 'Subunit of DNA', hint3: 'You inherit them' },
      { id: 'bio-4.1.3', type: 'choice', question: 'Which decides whether a baby is a boy?', options: ['Egg', 'Sperm', 'Womb', 'Mother\u2019s diet'], answer: 'Sperm', hint1: 'Carries X or Y', hint2: 'From the dad', hint3: 'Egg always carries X' },
    ],
    level2: [
      { id: 'bio-4.1.4', type: 'text', question: 'The full set of an organism\u2019s DNA is called its ___ .', answer: 'genome', hint1: '6 letters', hint2: 'gene + ome', hint3: 'Genome project' },
      { id: 'bio-4.1.5', type: 'choice', question: 'How many chromosomes are in a human sperm cell?', options: ['46', '23', '92', '24'], answer: '23', hint1: 'Half of body cell', hint2: 'One of each pair', hint3: 'Two digits' },
    ],
    level3: [
      { id: 'bio-4.1.6', type: 'choice', question: 'Why is the chance of a boy roughly 50% each time?', options: ['XX is twice as common', 'Each sperm carries either X or Y, in equal numbers', 'Eggs sometimes carry Y', 'It is random with no reason'], answer: 'Each sperm carries either X or Y, in equal numbers', hint1: 'Sperm are 50:50', hint2: 'Mum gives X always', hint3: 'Coin-flip per sperm' },
      { id: 'bio-4.1.7', type: 'text', question: 'The four DNA bases are A, T, C and ___ .', answer: 'G', hint1: 'Single letter', hint2: 'Pairs with C', hint3: 'Guanine' },
    ],
  },
};

// =====================================================================
// 4.2  VARIATION
// =====================================================================
const VARIATION_TOPIC = {
  id: 'bio-4.2',
  title: 'Variation',
  slug: 'variation',
  pageRef: 'Section 4 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is variation?',
        body:
          'Variation is the differences between individuals of the same species — height, eye colour, blood group, leaf size on the same plant. It comes from two sources: genes (inherited from parents) and the environment (food, exercise, sunlight, accidents). Most features are caused by a mix of both.',
        check: {
          question: 'Variation in eye colour is mostly caused by…',
          options: ['Genes', 'Environment', 'Diet', 'Practice'],
          answer: 'Genes',
          explainer: 'Eye colour is set by inherited DNA — diet won\u2019t change it.',
        },
      },
      {
        heading: 'Genetic vs environmental causes',
        body:
          'Some features are purely genetic (e.g. blood group, eye colour). Some are purely environmental (e.g. a scar from an accident). Many are both: height is set by genes BUT also affected by nutrition; weight is influenced by genes AND diet/exercise. Identical twins (same DNA) often differ in weight or accent because of environment.',
        check: {
          question: 'Two identical twins have different weights. Cause?',
          options: ['Genes', 'Environment', 'Mutations', 'Different chromosomes'],
          answer: 'Environment',
          explainer: 'Identical twins share the same DNA — any difference must come from environment (food, exercise, etc.).',
        },
      },
      {
        heading: 'Continuous vs discontinuous variation',
        body:
          'Continuous variation gives a smooth range — height, weight, hand span — usually plotted as a histogram and showing a normal distribution. Discontinuous variation gives distinct categories — blood group (A/B/AB/O), tongue rolling (yes/no) — usually plotted as a bar chart.',
        check: {
          question: 'Blood group is an example of…',
          options: ['Continuous variation', 'Discontinuous variation', 'No variation', 'Environmental variation'],
          answer: 'Discontinuous variation',
          explainer: 'You\u2019re A, B, AB or O — there\u2019s no in-between.',
        },
      },
    ],
    keyPoints: [
      'Variation = differences within a species',
      'Causes: genetic, environmental, or BOTH',
      'Continuous = smooth range (height) → histogram, normal distribution',
      'Discontinuous = categories (blood group) → bar chart',
      'Identical twins differ only because of environment',
    ],
    workedExamples: [
      {
        problem: 'A class measures hand spans (cm) and counts blood groups. Which would you plot as a histogram, and which as a bar chart? Why?',
        steps: [
          { step: 1, instruction: 'Hand span', working: 'Continuous (any value possible) → histogram' },
          { step: 2, instruction: 'Blood group', working: 'Discontinuous (only A/B/AB/O) → bar chart' },
        ],
        answer: 'Hand span = histogram (continuous). Blood group = bar chart (discontinuous).',
        commonMistake: 'Drawing histograms with gaps between bars — by definition, histograms have NO gaps.',
      },
    ],
    whyItWorks:
      'Variation is the raw material of evolution. Without it, every individual would be identical and natural selection would have nothing to act on. The mix of genes from two parents (plus mutations) keeps producing new combinations every generation.',
  },
  glossary: [
    { term: 'Variation', definition: 'Differences between individuals of the same species.' },
    { term: 'Genetic variation', definition: 'Differences caused by different genes.' },
    { term: 'Environmental variation', definition: 'Differences caused by the environment (food, climate, accidents).' },
    { term: 'Continuous variation', definition: 'Variation in a smooth range (e.g. height) shown as a histogram.' },
    { term: 'Discontinuous variation', definition: 'Variation in distinct categories (e.g. blood group) shown as a bar chart.' },
    { term: 'Identical twins', definition: 'Twins from one fertilised egg that split — same DNA.' },
  ],
  examQuestion: {
    prompt:
      'A class measures their heights (in cm) and records eye colour. (a) Classify each as continuous or discontinuous variation, with reasons. (b) Suggest TWO factors that affect adult height. (c) Explain why two siblings can have different heights even with the same parents. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Height = continuous (any value possible)',
      'Eye colour = discontinuous (distinct categories)',
      'Genes inherited from parents',
      'Environment: nutrition / exercise / illness in childhood',
      'Siblings get a different mix of alleles from each parent',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.2.w1', type: 'text', question: 'Differences within one species are called ___ .', answer: 'variation', hint1: '9 letters', hint2: 'Topic title', hint3: 'Starts with v' },
      { id: 'bio-4.2.w2', type: 'choice', question: 'Eye colour variation is mostly caused by…', options: ['Diet', 'Genes', 'Sunlight', 'Exercise'], answer: 'Genes', hint1: 'Inherited', hint2: '5 letters', hint3: 'DNA-based' },
      { id: 'bio-4.2.w3', type: 'choice', question: 'Tongue rolling is an example of…', options: ['Continuous variation', 'Discontinuous variation', 'Both', 'Neither'], answer: 'Discontinuous variation', hint1: 'Yes / no', hint2: 'Two categories', hint3: 'No middle ground' },
    ],
    level1: [
      { id: 'bio-4.2.1', type: 'choice', question: 'Height is variation that is BOTH genetic and environmental. The environmental factor is mostly…', options: ['Air temperature', 'Diet/nutrition', 'Eye colour', 'Blood group'], answer: 'Diet/nutrition', hint1: 'What you eat', hint2: 'Childhood', hint3: 'Calcium / protein' },
      { id: 'bio-4.2.2', type: 'choice', question: 'Continuous variation is best shown on a…', options: ['Pie chart', 'Bar chart with gaps', 'Histogram', 'Pictogram'], answer: 'Histogram', hint1: 'No gaps', hint2: 'Bars touching', hint3: 'Frequency vs range' },
      { id: 'bio-4.2.3', type: 'text', question: 'Variation caused by environment only is called ___ variation.', answer: 'environmental', hint1: '13 letters', hint2: 'Not genetic', hint3: 'Caused by surroundings' },
    ],
    level2: [
      { id: 'bio-4.2.4', type: 'choice', question: 'Identical twins raised apart often differ in weight. The cause is…', options: ['Mutations', 'Different DNA', 'Environment (diet/exercise)', 'Random chance'], answer: 'Environment (diet/exercise)', hint1: 'Same DNA', hint2: 'Lifestyle differs', hint3: 'Twin studies' },
      { id: 'bio-4.2.5', type: 'choice', question: 'Which is purely genetic?', options: ['Scars', 'Hairstyle', 'Blood group', 'Sun tan'], answer: 'Blood group', hint1: 'Set at conception', hint2: 'A/B/AB/O', hint3: 'No environmental input' },
    ],
    level3: [
      { id: 'bio-4.2.6', type: 'choice', question: 'Why is variation important for evolution?', options: ['Looks pretty', 'Provides differences for natural selection to act on', 'Reduces competition', 'Speeds up reproduction'], answer: 'Provides differences for natural selection to act on', hint1: 'Selection needs variation', hint2: 'Differences between individuals', hint3: 'Evolution depends on it' },
      { id: 'bio-4.2.7', type: 'text', question: 'Variation in a smooth range (any value) is called ___ variation.', answer: 'continuous', hint1: '10 letters', hint2: 'Smooth', hint3: 'Histogram-friendly' },
    ],
  },
};

// =====================================================================
// 4.3  INHERITANCE
// =====================================================================
const INHERITANCE_TOPIC = {
  id: 'bio-4.3',
  title: 'Inheritance',
  slug: 'inheritance',
  pageRef: 'Section 4 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Alleles, dominant and recessive',
        body:
          'Most genes come in two versions, called alleles — one inherited from each parent. Alleles are written as letters: a capital letter for a dominant allele (e.g. B for brown eyes), a lowercase for a recessive allele (b for blue). If you have at least one dominant allele, the dominant feature shows. You only show the recessive feature if BOTH alleles are recessive (bb).',
        check: {
          question: 'Which genotype shows the recessive feature?',
          options: ['BB', 'Bb', 'bB', 'bb'],
          answer: 'bb',
          explainer: 'You need TWO recessive alleles for the recessive feature to show.',
        },
      },
      {
        heading: 'Genotype vs phenotype',
        body:
          'Genotype = the alleles you carry (e.g. Bb). Phenotype = what you actually look like (e.g. brown eyes). Two organisms can share a phenotype but have different genotypes — both BB and Bb have brown eyes, but only Bb can pass on the blue allele.',
        check: {
          question: 'A person with genotype Bb has brown eyes. The phenotype is…',
          options: ['Bb', 'BB', 'Brown eyes', 'Blue eyes'],
          answer: 'Brown eyes',
          explainer: 'Phenotype is what shows; genotype is the alleles behind it.',
        },
      },
      {
        heading: 'Punnett squares',
        body:
          'A Punnett square is a 2×2 grid that lets you predict the chance of each genotype in offspring. List one parent\u2019s gametes along the top and the other parent\u2019s down the side. Fill in each box. Each box has a 25% (1 in 4) chance. Example: cross Bb × Bb → BB, Bb, Bb, bb. So 75% brown, 25% blue.',
        check: {
          question: 'Cross Bb × Bb. What % of offspring are predicted to be blue-eyed (bb)?',
          options: ['0%', '25%', '50%', '75%'],
          answer: '25%',
          explainer: 'Only 1 of the 4 boxes (bb) gives blue eyes.',
        },
      },
    ],
    keyPoints: [
      'Allele = one version of a gene; you have two (one from each parent)',
      'Capital = dominant; lowercase = recessive',
      'Recessive feature shows only if both alleles recessive (bb)',
      'Genotype = alleles; phenotype = appearance',
      'Punnett square = 2×2 grid to predict offspring ratios',
    ],
    workedExamples: [
      {
        problem: 'Brown (B) is dominant over blue (b) eyes. Predict the offspring of BB × bb.',
        steps: [
          { step: 1, instruction: 'BB gametes', working: 'B, B' },
          { step: 2, instruction: 'bb gametes', working: 'b, b' },
          { step: 3, instruction: 'Fill grid', working: 'Bb, Bb, Bb, Bb' },
        ],
        answer: '100% Bb → all brown-eyed (but all carriers of the blue allele).',
        commonMistake: 'Concluding that no blue-eyed children are EVER possible from this couple — true for THIS generation, but their children (Bb × Bb) could give 25% blue.',
      },
    ],
    whyItWorks:
      'Punnett squares work because of meiosis: gametes carry just ONE of each pair of alleles. Combining a random egg with a random sperm gives an equal chance for each combination. The maths is just like flipping coins.',
  },
  glossary: [
    { term: 'Allele', definition: 'A version of a gene (e.g. brown or blue eye allele).' },
    { term: 'Dominant', definition: 'The allele whose feature shows even with one copy (capital letter).' },
    { term: 'Recessive', definition: 'The allele whose feature only shows if BOTH copies are recessive (lowercase).' },
    { term: 'Genotype', definition: 'The alleles an organism carries (e.g. Bb).' },
    { term: 'Phenotype', definition: 'The actual feature shown (e.g. brown eyes).' },
    { term: 'Homozygous', definition: 'Two of the same allele (BB or bb).' },
    { term: 'Heterozygous', definition: 'Two different alleles (Bb).' },
    { term: 'Punnett square', definition: 'A grid used to predict offspring genotypes from a cross.' },
  ],
  examQuestion: {
    prompt:
      'In pea plants, tall (T) is dominant over short (t). A heterozygous tall plant (Tt) is crossed with a short plant (tt). (a) Draw the Punnett square. (b) Predict the % of tall offspring. (c) Define genotype and phenotype. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Punnett: Tt × tt → Tt, Tt, tt, tt',
      '50% tall (Tt) and 50% short (tt)',
      'Genotype = the alleles (Tt or tt)',
      'Phenotype = the actual feature shown (tall or short)',
      'Each box = 1/4 chance — must label correctly',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.3.w1', type: 'text', question: 'A version of a gene is called an ___ .', answer: 'allele', hint1: '6 letters', hint2: 'Two per gene', hint3: 'Starts with a' },
      { id: 'bio-4.3.w2', type: 'choice', question: 'Which letter shows a dominant allele?', options: ['Lowercase', 'Capital', 'Either', 'Neither'], answer: 'Capital', hint1: 'Big letter', hint2: 'Like B for brown', hint3: 'A, B, T...' },
      { id: 'bio-4.3.w3', type: 'choice', question: 'Genotype Bb gives the phenotype…', options: ['Blue eyes', 'Brown eyes', 'Both colours', 'No colour'], answer: 'Brown eyes', hint1: 'Dominant wins', hint2: 'B is dominant', hint3: 'Capital matters' },
    ],
    level1: [
      { id: 'bio-4.3.1', type: 'choice', question: 'Cross BB × bb. All offspring are…', options: ['BB', 'Bb', 'bb', 'A mix'], answer: 'Bb', hint1: 'Each parent passes one allele', hint2: 'B from one, b from other', hint3: 'All heterozygous' },
      { id: 'bio-4.3.2', type: 'number', question: 'Cross Bb × Bb. % of offspring with blue eyes (bb)?', answer: '25', hint1: 'One in four', hint2: 'Two digits', hint3: '25%' },
      { id: 'bio-4.3.3', type: 'text', question: 'Two of the same allele (BB or bb) is called ___ .', answer: 'homozygous', hint1: '10 letters', hint2: 'Same alleles', hint3: 'Starts with h' },
    ],
    level2: [
      { id: 'bio-4.3.4', type: 'choice', question: 'A child with bb has parents who are both Bb. The chance of this happening was…', options: ['0%', '25%', '50%', '75%'], answer: '25%', hint1: 'Punnett square', hint2: '1 in 4', hint3: 'Recessive feature' },
      { id: 'bio-4.3.5', type: 'choice', question: 'A "carrier" of a recessive condition has the genotype…', options: ['BB', 'Bb', 'bb', 'B'], answer: 'Bb', hint1: 'Has the allele but doesn\u2019t show', hint2: 'Heterozygous', hint3: 'One of each' },
    ],
    level3: [
      { id: 'bio-4.3.6', type: 'choice', question: 'Why can two brown-eyed parents have a blue-eyed child?', options: ['It is a mutation', 'Both parents are Bb carriers', 'The child has only one allele', 'The hospital made a mistake'], answer: 'Both parents are Bb carriers', hint1: 'Parents heterozygous', hint2: 'Punnett 25%', hint3: 'Recessive shows when both alleles recessive' },
      { id: 'bio-4.3.7', type: 'text', question: 'The actual appearance of a feature is called the ___ .', answer: 'phenotype', hint1: '9 letters', hint2: 'What you see', hint3: 'Starts with p' },
    ],
  },
};

// =====================================================================
// 4.4  NATURAL SELECTION
// =====================================================================
const NATURAL_SELECTION_TOPIC = {
  id: 'bio-4.4',
  title: 'Natural Selection',
  slug: 'natural-selection',
  pageRef: 'Section 4 · Topic 4',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Darwin\u2019s big idea',
        body:
          'In 1859 Charles Darwin proposed that species change over time by natural selection. Four steps: (1) more offspring are produced than can survive; (2) individuals show variation; (3) those with helpful traits survive longer and reproduce more; (4) their helpful alleles become more common in the next generation. Over many generations the species changes — that\u2019s evolution.',
        check: {
          question: 'Who proposed natural selection?',
          options: ['Mendel', 'Darwin', 'Newton', 'Watson'],
          answer: 'Darwin',
          explainer: 'Charles Darwin published "On the Origin of Species" in 1859.',
        },
      },
      {
        heading: 'Worked example: peppered moths',
        body:
          'Before the Industrial Revolution, most peppered moths were pale grey, blending in with lichen on tree bark. Pollution killed lichen and blackened the trees with soot. Pale moths now stood out and were eaten by birds; rare dark variants were now camouflaged. Within decades, the dark form dominated. After clean-air laws, lichen returned and the pale form recovered. Same species, same DNA pool — selection just shifted.',
        check: {
          question: 'In sooty industrial Britain, dark moths increased because…',
          options: ['They mutated more', 'They were better camouflaged so survived longer', 'They had more offspring per egg', 'Birds preferred pale ones to eat'],
          answer: 'They were better camouflaged so survived longer',
          explainer: 'Camouflage = harder to spot. Survivors reproduced more, so dark alleles spread.',
        },
      },
      {
        heading: 'Antibiotic resistance',
        body:
          'Bacteria reproduce rapidly. Random mutations sometimes give a bacterium resistance to an antibiotic. When the antibiotic is given, vulnerable bacteria die — but resistant ones survive and multiply. A whole population of resistant bacteria can develop in days. That\u2019s why we should only use antibiotics when needed, and finish the full course.',
        check: {
          question: 'Antibiotic resistance is an example of…',
          options: ['Photosynthesis', 'Natural selection', 'Genetic engineering', 'Selective breeding'],
          answer: 'Natural selection',
          explainer: 'Resistant bacteria survive while non-resistant ones die — that\u2019s selection in action.',
        },
      },
    ],
    keyPoints: [
      'Natural selection: variation → competition → survival → reproduction',
      'Darwin proposed it (1859)',
      'Peppered moths = classic British example',
      'Antibiotic resistance is selection happening NOW',
      'Helpful alleles become more common over generations',
    ],
    workedExamples: [
      {
        problem: 'Explain how a population of bacteria can become resistant to an antibiotic.',
        steps: [
          { step: 1, instruction: 'Variation', working: 'Random mutations create some resistant bacteria' },
          { step: 2, instruction: 'Selection pressure', working: 'Antibiotic kills non-resistant bacteria' },
          { step: 3, instruction: 'Reproduction', working: 'Resistant ones survive and reproduce, passing the resistance allele on' },
          { step: 4, instruction: 'Spread', working: 'Soon, the whole population is resistant' },
        ],
        answer: 'Mutation → resistant bacteria → antibiotic kills the rest → resistant ones reproduce → resistant population.',
        commonMistake: 'Saying bacteria "decide" to evolve. They don\u2019t — chance mutations + selection do all the work.',
      },
    ],
    whyItWorks:
      'Natural selection is automatic. It needs no designer. Wherever there\u2019s heritable variation and limited resources, the population that survives is the one best suited to the conditions. Change the conditions and a different variation wins.',
  },
  glossary: [
    { term: 'Natural selection', definition: 'Process where best-adapted individuals survive and pass their genes on.' },
    { term: 'Mutation', definition: 'A random change in DNA — the source of new variation.' },
    { term: 'Selection pressure', definition: 'A factor (predator, climate, antibiotic) that decides who survives.' },
    { term: 'Adaptation', definition: 'A feature that helps an organism survive in its environment.' },
    { term: 'Evolution', definition: 'Change in a species over many generations due to natural selection.' },
  ],
  examQuestion: {
    prompt:
      'A doctor warns that overuse of antibiotics is causing resistant bacteria. (a) Describe how natural selection has produced resistant bacteria. (b) Suggest TWO ways doctors and patients can slow down antibiotic resistance. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Mutation produces a few resistant bacteria',
      'Antibiotic kills non-resistant bacteria (selection pressure)',
      'Resistant bacteria reproduce and pass on the resistance allele',
      'Whole population becomes resistant',
      'Way 1: only prescribe antibiotics when truly needed',
      'Way 2: always finish the full course (any from: develop new antibiotics; restrict use in farming)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.4.w1', type: 'text', question: 'Who proposed natural selection?', answer: 'Darwin', hint1: '6 letters', hint2: 'Charles ___', hint3: 'On the Origin of Species' },
      { id: 'bio-4.4.w2', type: 'choice', question: 'Resistant bacteria survive antibiotics because of…', options: ['Diet', 'Mutations giving resistance', 'Cooler temperatures', 'Smaller cells'], answer: 'Mutations giving resistance', hint1: 'Random change in DNA', hint2: 'Source of new variation', hint3: 'Pre-existing chance' },
      { id: 'bio-4.4.w3', type: 'choice', question: 'Peppered moths went dark because…', options: ['Coal smoke painted them', 'Dark moths were camouflaged on sooty trees and survived', 'Light moths migrated', 'Birds preferred pale moths'], answer: 'Dark moths were camouflaged on sooty trees and survived', hint1: 'Camouflage', hint2: 'Less visible to predators', hint3: 'Survivors reproduced more' },
    ],
    level1: [
      { id: 'bio-4.4.1', type: 'text', question: 'A random change in DNA that creates new variation is called a ___ .', answer: 'mutation', hint1: '8 letters', hint2: 'Starts with m', hint3: 'Source of variation' },
      { id: 'bio-4.4.2', type: 'choice', question: 'Why do helpful alleles become more common over time?', options: ['Random luck', 'Carriers reproduce more', 'They mutate further', 'They produce more children faster'], answer: 'Carriers reproduce more', hint1: 'Selection rewards survival', hint2: 'Helpful = survives = breeds', hint3: 'Generations' },
      { id: 'bio-4.4.3', type: 'choice', question: 'Which of these is a selection pressure?', options: ['Predator', 'Pollution', 'Disease', 'All of the above'], answer: 'All of the above', hint1: 'Anything that affects survival', hint2: 'Multiple options', hint3: 'A, B, and C' },
    ],
    level2: [
      { id: 'bio-4.4.4', type: 'choice', question: 'In Darwin\u2019s 4-step model, the FIRST step is…', options: ['Reproduction', 'Variation', 'Survival of the fittest', 'Death of the weakest'], answer: 'Variation', hint1: 'Differences between individuals', hint2: 'Natural variation comes first', hint3: 'No selection without variation' },
      { id: 'bio-4.4.5', type: 'choice', question: 'Why is finishing your antibiotic course important?', options: ['It tastes better', 'To kill all bacteria, including any partially resistant ones', 'To impress the doctor', 'Antibiotics expire fast'], answer: 'To kill all bacteria, including any partially resistant ones', hint1: 'Avoid leaving survivors', hint2: 'Stops resistance evolving', hint3: 'Wipe out the population' },
    ],
    level3: [
      { id: 'bio-4.4.6', type: 'choice', question: 'Which best explains why bacteria evolve resistance so fast?', options: ['Bacteria are smart', 'Short generation times + huge populations', 'Antibiotics make bacteria evolve', 'They share alleles by touch'], answer: 'Short generation times + huge populations', hint1: 'Lots of generations per day', hint2: 'Quicker than humans', hint3: 'Numbers + speed' },
      { id: 'bio-4.4.7', type: 'text', question: 'Change in a species over many generations is called ___ .', answer: 'evolution', hint1: '9 letters', hint2: 'Long-term result', hint3: 'Starts with e' },
    ],
  },
};

// =====================================================================
// 4.5  EVOLUTION AND SPECIATION
// =====================================================================
const EVOLUTION_TOPIC = {
  id: 'bio-4.5',
  title: 'Evolution and Speciation',
  slug: 'evolution-and-speciation',
  pageRef: 'Section 4 · Topic 5',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What evolution actually is',
        body:
          'Evolution is the gradual change in the inherited features of a population over time. It happens because of natural selection acting on variation. Modern Earth has roughly 8 million species — all descended from a single common ancestor about 3.5 billion years ago. Evidence comes from fossils, DNA comparisons and observed selection (e.g. moths, antibiotic resistance).',
        check: {
          question: 'Evolution is best described as…',
          options: ['Sudden change', 'Change in a species\u2019 features over many generations', 'Animals choosing to evolve', 'God-given'],
          answer: 'Change in a species\u2019 features over many generations',
          explainer: 'Evolution is gradual, generational, and driven by selection — not a one-off event.',
        },
      },
      {
        heading: 'Evidence for evolution',
        body:
          'Three big lines of evidence: (1) Fossils — older rocks contain simpler organisms, more recent rocks contain forms similar to today\u2019s species. (2) DNA — closely related species share more DNA (humans and chimps ~99%). (3) Observed selection — peppered moths, antibiotic resistance, finch beaks on the Galapagos.',
        check: {
          question: 'Comparing DNA between species is evidence because…',
          options: ['Each species has unique DNA', 'Closely related species share more DNA', 'DNA shows the future', 'Mutations are random'],
          answer: 'Closely related species share more DNA',
          explainer: 'Common ancestry leaves a DNA fingerprint — the closer the relationship, the more shared sequence.',
        },
      },
      {
        heading: 'Speciation',
        body:
          'Speciation is the formation of a brand new species. The classic recipe: a population splits into two groups (e.g. by a mountain range, sea or river); each is now under different selection pressures; over many generations they evolve in different directions. If they become so different they can no longer interbreed to produce fertile offspring, they are now two separate species.',
        check: {
          question: 'Two populations are now separate species when…',
          options: ['They look different', 'They live in different places', 'They cannot interbreed to produce fertile offspring', 'They eat different foods'],
          answer: 'They cannot interbreed to produce fertile offspring',
          explainer: 'Reproductive isolation = the textbook definition of separate species.',
        },
      },
    ],
    keyPoints: [
      'Evolution = change in features over generations',
      'Evidence: fossils, DNA, observed selection',
      'Humans + chimps ~99% DNA = recent common ancestor',
      'Speciation: isolation → different selection → reproductive isolation',
      'New species = no longer interbreed to fertile young',
    ],
    workedExamples: [
      {
        problem: 'A population of finches is split between two islands by rising sea levels. After a million years, the two groups can no longer interbreed. Explain how speciation occurred.',
        steps: [
          { step: 1, instruction: 'Isolation', working: 'Sea split the population — no gene flow between groups' },
          { step: 2, instruction: 'Different selection', working: 'Each island has different food/predators/climate' },
          { step: 3, instruction: 'Different alleles dominate', working: 'Each population evolves separately' },
          { step: 4, instruction: 'Reproductive isolation', working: 'After enough change they cannot interbreed → two species' },
        ],
        answer: 'Geographical isolation → different selection pressures → genetic drift → reproductive isolation → two species.',
        commonMistake: 'Forgetting that the populations must be physically isolated FIRST, otherwise they keep mixing genes.',
      },
    ],
    whyItWorks:
      'Speciation explains how Earth got from one common ancestor to ~8 million species. Every dividing population is a fork in the tree of life. Some forks die out; some keep splitting. The whole tree is the family tree of life.',
  },
  glossary: [
    { term: 'Evolution', definition: 'The gradual change in features of a species over generations.' },
    { term: 'Speciation', definition: 'The formation of a new species.' },
    { term: 'Common ancestor', definition: 'A species from which two or more later species descended.' },
    { term: 'Reproductive isolation', definition: 'When two groups can no longer interbreed to produce fertile offspring.' },
    { term: 'Fossil', definition: 'The preserved remains or trace of a long-dead organism.' },
  ],
  examQuestion: {
    prompt:
      '(a) Give TWO pieces of evidence for evolution. (b) Describe the steps that lead from one population to two new species. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Fossil record shows simpler organisms in older rocks',
      'DNA — closely related species share more sequence',
      'Geographical isolation of two groups',
      'Different selection pressures in each environment',
      'Build-up of differences until reproductive isolation → 2 species',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.5.w1', type: 'text', question: 'Formation of a new species is called ___ .', answer: 'speciation', hint1: '10 letters', hint2: 'Like \u201cspecies\u201d but verb', hint3: 'Starts with s' },
      { id: 'bio-4.5.w2', type: 'choice', question: 'Which is evidence for evolution?', options: ['Fossils', 'DNA', 'Observed selection', 'All of the above'], answer: 'All of the above', hint1: 'Three lines listed', hint2: 'Triple-confirm', hint3: 'A + B + C' },
      { id: 'bio-4.5.w3', type: 'choice', question: 'Older rocks contain…', options: ['No fossils', 'More complex organisms', 'Simpler organisms', 'Only humans'], answer: 'Simpler organisms', hint1: 'Earlier life', hint2: 'Less developed', hint3: 'Bacteria, simple plants' },
    ],
    level1: [
      { id: 'bio-4.5.1', type: 'choice', question: 'Two populations become separate species when…', options: ['They live in different places', 'They look slightly different', 'They cannot produce fertile offspring together', 'They eat different food'], answer: 'They cannot produce fertile offspring together', hint1: 'Reproductive isolation', hint2: 'Cannot interbreed', hint3: 'Definition of new species' },
      { id: 'bio-4.5.2', type: 'text', question: 'Humans and chimps share roughly ___ % of their DNA.', answer: '99', hint1: 'Two digits', hint2: 'Just under 100', hint3: '~99%' },
    ],
    level2: [
      { id: 'bio-4.5.3', type: 'choice', question: 'Speciation usually starts with…', options: ['Random mutation', 'Geographical isolation', 'Sudden death', 'Hybridisation'], answer: 'Geographical isolation', hint1: 'Population split', hint2: 'Mountain, sea, river', hint3: 'Stops gene flow' },
      { id: 'bio-4.5.4', type: 'choice', question: 'Why does isolation lead to two species over time?', options: ['Each evolves under different pressures', 'Gravity changes', 'They share alleles less', 'Both A and C'], answer: 'Both A and C', hint1: 'Two reasons true', hint2: 'No gene flow + different selection', hint3: 'Combined cause' },
    ],
    level3: [
      { id: 'bio-4.5.5', type: 'choice', question: 'Which is the strongest molecular evidence two species share a common ancestor?', options: ['Same fossils', 'Live in same area', 'Similar DNA sequences', 'Same diet'], answer: 'Similar DNA sequences', hint1: 'Inherited code', hint2: 'Closer = more shared', hint3: 'Modern technique' },
      { id: 'bio-4.5.6', type: 'text', question: 'A species two later species both descended from is their ___ ancestor.', answer: 'common', hint1: '6 letters', hint2: 'Shared by both', hint3: 'Tree of life term' },
    ],
  },
};

// =====================================================================
// 4.6  EXTINCTION
// =====================================================================
const EXTINCTION_TOPIC = {
  id: 'bio-4.6',
  title: 'Extinction',
  slug: 'extinction',
  pageRef: 'Section 4 · Topic 6',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What extinction means',
        body:
          'A species is extinct when no living individuals remain. About 99% of all species that have ever lived are now extinct. Extinction is a natural part of evolution, but human activity is now driving species extinct hundreds of times faster than the natural background rate.',
        check: {
          question: 'A species is extinct when…',
          options: ['It is rare', 'No living individuals remain', 'It only lives in zoos', 'Its habitat shrinks'],
          answer: 'No living individuals remain',
          explainer: 'Extinction is permanent — a species "dying out" entirely.',
        },
      },
      {
        heading: 'Natural causes of extinction',
        body:
          'Climate change (the dinosaurs were hit by an asteroid 66 million years ago that triggered a global cooling); new predators/competitors arriving; new diseases; loss of food. Mass extinctions in Earth\u2019s history wiped out 50–90% of species in short bursts.',
        check: {
          question: 'What killed off the dinosaurs?',
          options: ['Hunting by mammals', 'A massive volcanic and asteroid event 66 million years ago', 'A flood', 'Sudden cold snap last winter'],
          answer: 'A massive volcanic and asteroid event 66 million years ago',
          explainer: 'The K-Pg event blocked sunlight, cooled the planet and crashed food chains.',
        },
      },
      {
        heading: 'Human-driven extinction',
        body:
          'Humans cause extinctions through habitat destruction (deforestation, draining wetlands), hunting (the dodo, passenger pigeon), pollution (chemicals, plastics), introduced species (cats and rats killing native island birds), and climate change. Conservation aims to slow this — protected areas, captive breeding, gene banks.',
        check: {
          question: 'Which is NOT a major human cause of extinction?',
          options: ['Habitat destruction', 'Hunting', 'Photosynthesis', 'Climate change'],
          answer: 'Photosynthesis',
          explainer: 'Photosynthesis is something plants DO — it doesn\u2019t cause extinction.',
        },
      },
    ],
    keyPoints: [
      'Extinct = no living individuals of a species remain',
      '~99% of all species that ever lived are extinct',
      'Natural causes: climate change, predators, disease',
      'Human causes: habitat loss, hunting, pollution, invasive species, climate change',
      'Conservation aims to prevent extinction',
    ],
    workedExamples: [
      {
        problem: 'The dodo went extinct around 1681 on Mauritius. Suggest THREE reasons why.',
        steps: [
          { step: 1, instruction: 'Hunting', working: 'Sailors killed dodos easily for food' },
          { step: 2, instruction: 'Invasive species', working: 'Dogs, cats, rats and pigs ate dodo eggs and chicks' },
          { step: 3, instruction: 'Habitat loss', working: 'Forests were cleared for farms and settlements' },
        ],
        answer: 'Hunting + invasive species predating eggs + habitat destruction.',
        commonMistake: 'Saying "old age" — extinction is about a species, not an individual.',
      },
    ],
    whyItWorks:
      'Extinction is the OFF switch of evolution. When conditions change too fast for selection to keep up, species die out. Today, climate change is moving so fast that thousands of species can\u2019t adapt in time — which is why conservation matters.',
  },
  glossary: [
    { term: 'Extinct', definition: 'A species with no living members left.' },
    { term: 'Mass extinction', definition: 'An event where a huge proportion of species die out in a short time.' },
    { term: 'Habitat destruction', definition: 'Removing the place where a species lives (e.g. deforestation).' },
    { term: 'Invasive species', definition: 'A species introduced to a new place that disrupts the local ecosystem.' },
    { term: 'Conservation', definition: 'Action taken to protect species and habitats from extinction.' },
  ],
  examQuestion: {
    prompt:
      '(a) Define extinction. (b) Explain TWO ways human activity is causing extinctions today. (c) Suggest ONE thing humans could do to reduce extinction rates. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Extinct = no living individuals of a species remain',
      'Habitat destruction (e.g. clearing forests for farming)',
      'Climate change OR pollution OR overhunting OR invasive species (any 2)',
      'Reduces resources / prey / nesting sites — population crashes',
      'Solution: protected areas / captive breeding / reduced emissions / banning ivory etc.',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.6.w1', type: 'text', question: 'A species with no living members is ___ .', answer: 'extinct', hint1: '7 letters', hint2: 'Permanent', hint3: 'Ends with t' },
      { id: 'bio-4.6.w2', type: 'choice', question: 'Roughly what % of species ever to live are extinct?', options: ['1%', '50%', '99%', '100%'], answer: '99%', hint1: 'Almost all', hint2: 'Two digits + %', hint3: 'Surprising answer' },
      { id: 'bio-4.6.w3', type: 'choice', question: 'Which is a HUMAN cause of extinction?', options: ['Asteroid', 'Hunting', 'Volcanoes', 'Earthquake'], answer: 'Hunting', hint1: 'Driven by people', hint2: 'Dodo example', hint3: 'Started with h' },
    ],
    level1: [
      { id: 'bio-4.6.1', type: 'choice', question: 'The dodo became extinct mainly due to…', options: ['Old age', 'Lightning', 'Humans (hunting + invasive species)', 'Volcanoes'], answer: 'Humans (hunting + invasive species)', hint1: 'On Mauritius', hint2: 'Sailors + their pets', hint3: 'Multiple human pressures' },
      { id: 'bio-4.6.2', type: 'text', question: 'A new species introduced to an area that harms native life is called an ___ species.', answer: 'invasive', hint1: '8 letters', hint2: 'Take-over', hint3: 'Like rats on islands' },
    ],
    level2: [
      { id: 'bio-4.6.3', type: 'choice', question: 'Why might climate change cause extinction?', options: ['Animals get tired', 'Habitats change faster than species can adapt', 'Sun goes out', 'Magnetic field reverses'], answer: 'Habitats change faster than species can adapt', hint1: 'Selection too slow', hint2: 'Mismatch', hint3: 'Cannot evolve in time' },
      { id: 'bio-4.6.4', type: 'choice', question: 'Which is a conservation action?', options: ['Logging old forests', 'Captive breeding programmes', 'Releasing pets into the wild', 'Adding fertiliser to lakes'], answer: 'Captive breeding programmes', hint1: 'Breed in zoos', hint2: 'Release later', hint3: 'Pandas, condors' },
    ],
    level3: [
      { id: 'bio-4.6.5', type: 'choice', question: 'Which mass extinction wiped out the dinosaurs?', options: ['Permian–Triassic', 'Cretaceous–Paleogene (66 mya)', 'Ordovician–Silurian', 'Future-Anthropocene'], answer: 'Cretaceous–Paleogene (66 mya)', hint1: 'K-Pg', hint2: 'Asteroid + volcanism', hint3: '66 million years ago' },
      { id: 'bio-4.6.6', type: 'text', question: 'An action taken to protect species and habitats is called ___ .', answer: 'conservation', hint1: '12 letters', hint2: 'Saves species', hint3: 'Starts with c' },
    ],
  },
};

// =====================================================================
// 4.7  FOSSILS
// =====================================================================
const FOSSILS_TOPIC = {
  id: 'bio-4.7',
  title: 'Fossils and Preserved Species',
  slug: 'fossils-and-preserved-species',
  pageRef: 'Section 4 · Topic 7',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a fossil?',
        body:
          'A fossil is the preserved remains, trace or imprint of an organism that lived long ago — usually thousands or millions of years old. Most fossils are in sedimentary rock. Younger fossils sit higher up; older fossils sit deeper. By comparing layers, we can see how species have changed over time.',
        check: {
          question: 'In layered sedimentary rock, the OLDEST fossils are usually…',
          options: ['At the top', 'In the middle', 'At the bottom', 'Mixed up everywhere'],
          answer: 'At the bottom',
          explainer: 'New layers form on top of old ones, so older fossils end up deeper.',
        },
      },
      {
        heading: 'How fossils form',
        body:
          'Three main routes: (1) Hard parts replaced by minerals — the original bone or shell is slowly replaced by minerals, leaving a stone copy of the original shape. (2) Imprints — a soft body presses into mud which then hardens to rock (e.g. ferns, footprints). (3) Whole-body preservation — frozen mammoths in ice, insects trapped in tree resin (amber).',
        check: {
          question: 'A 10,000-year-old mammoth found frozen in ice is preserved by…',
          options: ['Mineralisation', 'An imprint', 'Freezing (whole-body preservation)', 'Burning'],
          answer: 'Freezing (whole-body preservation)',
          explainer: 'Freezing stops bacteria from decomposing the soft tissue.',
        },
      },
      {
        heading: 'Why the fossil record is incomplete',
        body:
          'Fossilisation is rare — most dead organisms decompose before they can be preserved. Soft-bodied animals (jellyfish, worms) leave hardly any fossils. Fossils can also be destroyed when rocks are heated, crushed or eroded. So the fossil record gives us a great sketch of evolution but never the full picture.',
        check: {
          question: 'Why do soft-bodied organisms leave fewer fossils?',
          options: ['They never died', 'They decompose before they can be preserved', 'Their cells contain no DNA', 'They aren\u2019t real species'],
          answer: 'They decompose before they can be preserved',
          explainer: 'Without bones or shells, bacteria break them down quickly.',
        },
      },
    ],
    keyPoints: [
      'Fossil = preserved remains/trace/imprint of an old organism',
      'Most fossils in sedimentary rock; older fossils deeper',
      '3 routes: minerals replacing hard parts, imprints, whole-body preservation',
      'Fossilisation is rare → record is incomplete',
      'Soft bodies rarely fossilise',
    ],
    workedExamples: [
      {
        problem: 'A fossil bone is found 30 m below the surface; a similar one is found 5 m below. Which is older, and how do you know?',
        steps: [
          { step: 1, instruction: 'Apply rule', working: 'Sedimentary rock: deeper = older' },
          { step: 2, instruction: 'Compare', working: '30 m is much deeper than 5 m' },
        ],
        answer: 'The 30 m bone is older.',
        commonMistake: 'Saying it depends on the rock type — for normal sedimentary layering, depth = age.',
      },
    ],
    whyItWorks:
      'Fossils are time machines. They show what life looked like at different points in Earth\u2019s history, and how species have changed. The fossil record is one of the strongest pieces of evidence for evolution — even if it has gaps.',
  },
  glossary: [
    { term: 'Fossil', definition: 'The preserved remains, trace or imprint of a dead organism.' },
    { term: 'Sedimentary rock', definition: 'Rock formed in layers from compressed sediment — most fossils are found here.' },
    { term: 'Mineralisation', definition: 'A process where minerals replace hard parts of an organism, turning them to stone.' },
    { term: 'Amber', definition: 'Hardened tree resin that can preserve trapped insects perfectly.' },
    { term: 'Fossil record', definition: 'The collection of all known fossils — used as evidence of evolution.' },
  ],
  examQuestion: {
    prompt:
      '(a) Describe TWO ways a fossil can form. (b) Explain why the fossil record is described as "incomplete". (c) State how scientists know one fossil is older than another. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Way 1: Hard parts replaced by minerals',
      'Way 2: Soft body imprint preserved as rock OR whole-body preservation in ice/amber',
      'Soft bodies rarely fossilise',
      'Fossils can be destroyed by heat / pressure / erosion',
      'Deeper rock layers are older (sedimentary rules)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.7.w1', type: 'text', question: 'Most fossils are found in ___ rock.', answer: 'sedimentary', hint1: '11 letters', hint2: 'Layered', hint3: 'Compressed sand/mud' },
      { id: 'bio-4.7.w2', type: 'choice', question: 'Older fossils are usually…', options: ['At the top', 'At the bottom', 'In the middle', 'Random'], answer: 'At the bottom', hint1: 'Deeper layers', hint2: 'Older below new', hint3: 'Sedimentary rule' },
      { id: 'bio-4.7.w3', type: 'choice', question: 'A mammoth found in ice is preserved by…', options: ['Mineralisation', 'Freezing', 'An imprint', 'Burning'], answer: 'Freezing', hint1: 'Cold stops decay', hint2: 'Whole-body preservation', hint3: 'Arctic conditions' },
    ],
    level1: [
      { id: 'bio-4.7.1', type: 'choice', question: 'Why do soft bodies rarely fossilise?', options: ['They float away', 'They decompose too fast', 'They have no DNA', 'They\u2019re too soft for bacteria'], answer: 'They decompose too fast', hint1: 'No bones/shells', hint2: 'Bacteria win', hint3: 'Decay before preservation' },
      { id: 'bio-4.7.2', type: 'text', question: 'Insects perfectly preserved in tree resin are found in ___ .', answer: 'amber', hint1: '5 letters', hint2: 'Hardened resin', hint3: 'Yellow / orange' },
    ],
    level2: [
      { id: 'bio-4.7.3', type: 'choice', question: 'Why is the fossil record incomplete?', options: ['Too many fossils to look at', 'Most organisms never fossilise', 'Aliens take them', 'Rocks are too soft'], answer: 'Most organisms never fossilise', hint1: 'Rare event', hint2: 'Decomposition + erosion', hint3: 'Gaps in record' },
      { id: 'bio-4.7.4', type: 'choice', question: 'What process turns bone into stone over millions of years?', options: ['Photosynthesis', 'Respiration', 'Mineralisation', 'Combustion'], answer: 'Mineralisation', hint1: 'Minerals replace bone', hint2: 'Stone copy', hint3: 'Slow chemistry' },
    ],
    level3: [
      { id: 'bio-4.7.5', type: 'choice', question: 'Which is the BEST evidence from fossils that species have changed over time?', options: ['Same species in every layer', 'Older layers contain simpler/different species', 'Fossils are all the same age', 'Fossils only of mammals'], answer: 'Older layers contain simpler/different species', hint1: 'Comparing layers', hint2: 'Sequence over time', hint3: 'Trends in record' },
      { id: 'bio-4.7.6', type: 'text', question: 'The collection of all known fossils is called the fossil ___ .', answer: 'record', hint1: '6 letters', hint2: 'Like a database', hint3: 'Starts with r' },
    ],
  },
};

// =====================================================================
// 4.8  SELECTIVE BREEDING
// =====================================================================
const SELECTIVE_BREEDING_TOPIC = {
  id: 'bio-4.8',
  title: 'Selective Breeding',
  slug: 'selective-breeding',
  pageRef: 'Section 4 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is selective breeding?',
        body:
          'Selective breeding (also called artificial selection) is when humans choose which plants or animals to breed together, in order to keep useful features. Examples: cows that produce more milk, wheat with more grain per plant, dogs of a particular shape or temperament. Whatever feature is wanted, only individuals showing it are allowed to breed.',
        check: {
          question: 'Selective breeding is also called…',
          options: ['Natural selection', 'Artificial selection', 'Genetic engineering', 'Cloning'],
          answer: 'Artificial selection',
          explainer: 'Humans replace nature as the "selector" — same idea, controlled by people.',
        },
      },
      {
        heading: 'How it\u2019s done',
        body:
          'Step 1: choose parents that show the desired feature most strongly. Step 2: breed only those parents. Step 3: from the offspring, pick the ones that show the feature even more strongly and breed them. Repeat for many generations. Over time, the feature becomes very common (or extreme) in the population.',
        check: {
          question: 'In selective breeding, which individuals are allowed to reproduce?',
          options: ['All of them', 'Only those with the wanted feature', 'Only the smallest', 'Only the youngest'],
          answer: 'Only those with the wanted feature',
          explainer: 'That\u2019s the whole point — only the chosen ones reproduce so their alleles spread.',
        },
      },
      {
        heading: 'Pros and problems',
        body:
          'Pros: more food (high-yield crops, beefier cows), useful working animals (sheepdogs), pest-resistant plants. Problems: reduces genetic variation (everyone shares similar alleles), so a new disease can wipe out a whole crop or breed (e.g. potato blight in the 1840s). Some pedigree dog breeds also suffer health problems from inbreeding.',
        check: {
          question: 'Why is reduced genetic variation a risk?',
          options: ['Animals get bored', 'A new disease can wipe out the whole population', 'Crops grow faster', 'Animals stop reproducing'],
          answer: 'A new disease can wipe out the whole population',
          explainer: 'If everyone has the same alleles, no individuals are resistant to a brand-new disease.',
        },
      },
    ],
    keyPoints: [
      'Selective breeding = humans choose who breeds',
      'Repeat over generations to make a feature stronger',
      'Pros: food, livestock, pets, pest resistance',
      'Cons: reduces genetic variation → disease vulnerability',
      'Same mechanism as natural selection — different selector',
    ],
    workedExamples: [
      {
        problem: 'A farmer wants cows that give more milk. Describe how to selectively breed them over 5 generations.',
        steps: [
          { step: 1, instruction: 'Identify trait', working: 'High-milk-yield cows' },
          { step: 2, instruction: 'Choose parents', working: 'Pick the highest-yield cow + a bull from a high-yield mother' },
          { step: 3, instruction: 'Breed and select', working: 'From the calves, again pick the highest-yield ones' },
          { step: 4, instruction: 'Repeat', working: 'Continue for 5+ generations — average yield rises each time' },
        ],
        answer: 'Pick high-yield parents → breed → select best offspring → repeat for many generations.',
        commonMistake: 'Calling this "evolution" — it IS a kind of evolution, but driven by humans, not nature.',
      },
    ],
    whyItWorks:
      'Selective breeding works because of variation and inheritance — if a desirable feature is heritable (caused by genes), choosing the parents = controlling which alleles get into the next generation. Repeat thousands of times and you get a chihuahua from a wolf.',
  },
  glossary: [
    { term: 'Selective breeding', definition: 'Humans choose which individuals breed together to keep wanted features. Also called artificial selection.' },
    { term: 'Yield', definition: 'How much useful product (milk, grain, meat) is produced.' },
    { term: 'Pedigree', definition: 'A breed with a documented family tree (often inbred).' },
    { term: 'Inbreeding', definition: 'Breeding of close relatives — reduces genetic variation.' },
    { term: 'Genetic variation', definition: 'Differences in alleles between individuals of a species.' },
  ],
  examQuestion: {
    prompt:
      'A farmer selectively breeds wheat for higher grain yield over 20 generations. (a) Describe the steps the farmer takes. (b) Explain ONE benefit and ONE risk of selective breeding. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Pick wheat plants with the highest grain yield',
      'Breed only those plants together',
      'From offspring, pick the highest yielders again, repeat for many generations',
      'Benefit: more food / higher yield / can feed more people',
      'Risk: reduced genetic variation → vulnerable to a new disease',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-4.8.w1', type: 'text', question: 'Selective breeding is also called ___ selection.', answer: 'artificial', hint1: '10 letters', hint2: 'Opposite of natural', hint3: 'Made by humans' },
      { id: 'bio-4.8.w2', type: 'choice', question: 'Which is an example of selective breeding?', options: ['Polar bears in the Arctic', 'High-milk-yield cows', 'Wild rabbits', 'Antibiotic-resistant bacteria'], answer: 'High-milk-yield cows', hint1: 'Humans intervene', hint2: 'Domestic animal', hint3: 'Farming aim' },
      { id: 'bio-4.8.w3', type: 'choice', question: 'In selective breeding, only ___ individuals reproduce.', options: ['Random', 'The biggest', 'Those with the wanted feature', 'The oldest'], answer: 'Those with the wanted feature', hint1: 'Selected ones', hint2: 'Show desired trait', hint3: 'Match the goal' },
    ],
    level1: [
      { id: 'bio-4.8.1', type: 'choice', question: 'A risk of long-term selective breeding is…', options: ['Higher yields', 'Reduced genetic variation', 'Faster growth', 'Easier farming'], answer: 'Reduced genetic variation', hint1: 'Down side', hint2: 'Same alleles', hint3: 'Bad if disease arrives' },
      { id: 'bio-4.8.2', type: 'text', question: 'Breeding of close relatives is called ___ .', answer: 'inbreeding', hint1: '10 letters', hint2: 'Shared family tree', hint3: 'Pedigree problem' },
    ],
    level2: [
      { id: 'bio-4.8.3', type: 'choice', question: 'Why are some pedigree dogs prone to health problems?', options: ['They eat strangely', 'Inbreeding makes harmful alleles common', 'They live in cities', 'No reason'], answer: 'Inbreeding makes harmful alleles common', hint1: 'Less variation', hint2: 'Recessive issues surface', hint3: 'Closed gene pool' },
      { id: 'bio-4.8.4', type: 'choice', question: 'Selective breeding works because…', options: ['Features are heritable', 'Animals decide to change', 'Magic', 'Climate change'], answer: 'Features are heritable', hint1: 'Genes pass on', hint2: 'Inheritance', hint3: 'Linked to alleles' },
    ],
    level3: [
      { id: 'bio-4.8.5', type: 'choice', question: 'Which feature has NOT been bred into modern crops?', options: ['Higher yield', 'Pest resistance', 'Larger fruit', 'Photosynthesis'], answer: 'Photosynthesis', hint1: 'All plants already do this', hint2: 'Not a bred trait', hint3: 'Already universal' },
      { id: 'bio-4.8.6', type: 'text', question: 'A breed with a documented family tree is called a ___ breed.', answer: 'pedigree', hint1: '8 letters', hint2: 'Tracked ancestry', hint3: 'Starts with p' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  DNA_TOPIC,
  VARIATION_TOPIC,
  INHERITANCE_TOPIC,
  NATURAL_SELECTION_TOPIC,
  EVOLUTION_TOPIC,
  EXTINCTION_TOPIC,
  FOSSILS_TOPIC,
  SELECTIVE_BREEDING_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const inheritanceVariationSurvivalTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
